import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Handle token refresh errors
        if (event === 'TOKEN_REFRESHED' && !session) {
          // Token refresh failed, clear session
          supabase.auth.signOut();
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      // Handle invalid refresh token error
      if (error?.message?.includes('refresh_token') || error?.code === 'refresh_token_not_found') {
        logger.warn('Invalid refresh token, clearing session', undefined, 'Auth');
        supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
        },
      },
    });
    
    // Check if user already exists but email not confirmed
    // Supabase returns a user with identities = [] in this case
    const userAlreadyExists = data?.user?.identities?.length === 0;
    
    return { error, userAlreadyExists };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?mode=reset`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  const signInWithMagicLink = async (email: string, redirectTo?: string) => {
    const redirectUrl = redirectTo || `${window.location.origin}/`;

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const updateEmail = async (newEmail: string) => {
    const redirectUrl = `${window.location.origin}/profile?email-updated=true`;
    
    const { error } = await supabase.auth.updateUser(
      { email: newEmail },
      { emailRedirectTo: redirectUrl }
    );
    
    return { error };
  };

  const signUpWithInvitation = async (
    email: string, 
    password: string, 
    workspaceId: string, 
    token: string,
    firstName?: string,
    lastName?: string
  ) => {
    try {
      const { data, error } = await supabase.functions.invoke('signup-with-invitation', {
        body: { email, password, workspaceId, token, firstName, lastName },
      });

      if (error) {
        return {
          error: { message: error.message || 'Signup failed' },
          session: null,
          userExists: false,
        };
      }

      // The edge function returns `code: 'user_exists'` with status 200 when
      // the email already has an account. We surface that as an error with the
      // userExists flag so Signup.tsx can redirect to /auth.
      if (data?.code === 'user_exists') {
        return {
          error: { message: data?.error || 'A user with this email already exists' },
          session: null,
          userExists: true,
        };
      }

      if (data?.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      return {
        error: null,
        session: data?.session,
        userExists: false,
      };
    } catch (err) {
      return { 
        error: { message: 'An unexpected error occurred' }, 
        session: null,
        userExists: false
      };
    }
  };

  const signInWithGoogle = async (redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo || `${window.location.origin}/`,
      },
    });
    return { error };
  };

  return {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    updateEmail,
    signInWithMagicLink,
    signUpWithInvitation,
    signInWithGoogle,
  };
};
